import { FormSchemaFields } from '@stormwater-software/lib-form';
import { pick } from 'lodash';
import { object } from 'prop-types';
import React from 'react';

/**
 * `FormSchemaFields`, with `initialFormData` narrowed to the fields the
 * widget's own `schema` renders.
 *
 * Why (SW2-2229): lib-form's `FormSchemaFields` runs a mount-only effect that
 * pushes *every* key of `initialFormData` back through `onChange`
 * (`@stormwater-software/lib-form/src/schema/SchemaFields.tsx`, and the same
 * loop in the shipped `dist` build). When several of these widgets write into
 * one shared `FormContext` and each is handed the whole record — as the ~10
 * widgets on `ClientSettingsTab` were — any widget that mounts late re-stamps
 * fields owned by *other* sections, silently discarding whatever the user had
 * already typed into them.
 *
 * Narrowing loses nothing. The only other reader of `initialFormData` is
 * `initialValue` on each rendered `FormGroup`, which reads schema fields only;
 * `pick` keeps exactly those, so every `FormGroup` still sees the value it saw
 * before. The shared form is seeded with the full record by the caller's
 * `FormContextProvider` (`ClientSettingsTab` does this), so no field loses its
 * initial value by being dropped here.
 *
 * This is only half of SW2-2229, and not the half that caused the reported
 * symptom: `FormContextProvider` *also* re-stamps every key of its
 * `initialValue` whenever that prop changes identity, which fires on every
 * render unless the caller keeps the object stable. See the `useMemo` on
 * `initialValue` in `ClientSettingsTab.jsx`; a caller that skips it still loses
 * user input no matter what this wrapper does.
 *
 * Prefer this over `FormSchemaFields` wherever more than one widget shares a
 * form context.
 */
export function ScopedSchemaFields({ schema, initialFormData, ...props }) {
  return (
    <FormSchemaFields
      schema={schema}
      initialFormData={pick(initialFormData, Object.keys(schema))}
      {...props}
    />
  );
}

ScopedSchemaFields.propTypes = {
  schema: object.isRequired,
  initialFormData: object,
};

export default ScopedSchemaFields;
